import { useEffect, useState } from "react";
import { Globe, ChevronDown, ChevronUp, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const Lbl = ({ children }) => <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>;

const labelFor = (key) => {
  if (key === "global") return "Réglage global (par défaut)";
  if (key === "home") return "Page d'accueil";
  if (key === "boutique") return "Boutique";
  if (key.startsWith("category:")) return `Catégorie · ${key.split(":")[1]}`;
  return key;
};

function SeoRow({ entry, onSave }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(entry);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    await onSave(entry.key, {
      title: form.title || "", description: form.description || "",
      keywords: form.keywords || "", og_image: form.og_image || "",
    });
    setSaving(false);
  };

  return (
    <div className="bg-white border border-stone rounded-sm" data-testid={`seo-row-${entry.key}`}>
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between p-4 text-left">
        <div>
          <p className="font-medium text-ink">{labelFor(entry.key)}</p>
          <p className="text-xs text-ash mt-0.5 truncate max-w-md">{form.title || "—"}</p>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-ash" /> : <ChevronDown className="h-5 w-5 text-ash" />}
      </button>
      {open && (
        <div className="border-t border-stone p-4 space-y-4">
          <div><Lbl>Titre (meta title)</Lbl><Input data-testid={`seo-title-${entry.key}`} value={form.title || ""} onChange={set("title")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
          <div><Lbl>Meta description</Lbl><Textarea data-testid={`seo-desc-${entry.key}`} value={form.description || ""} onChange={set("description")} rows={2} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
          <div><Lbl>Mots-clés (séparés par des virgules)</Lbl><Input data-testid={`seo-keywords-${entry.key}`} value={form.keywords || ""} onChange={set("keywords")} className="mt-1.5 rounded-sm border-stone bg-white" /></div>
          <div><Lbl>Image de partage (Open Graph) — URL</Lbl><Input data-testid={`seo-ogimage-${entry.key}`} value={form.og_image || ""} onChange={set("og_image")} placeholder="/icon-512.png ou https://..." className="mt-1.5 rounded-sm border-stone bg-white" /></div>
          <button data-testid={`seo-save-${entry.key}`} onClick={save} disabled={saving} className="flex items-center gap-2 bg-ink text-cream px-6 py-3 uppercase tracking-[0.12em] text-xs hover:bg-gold hover:text-ink transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> {saving ? "..." : "Enregistrer"}
          </button>
        </div>
      )}
    </div>
  );
}

export default function SuperSeo() {
  const { user } = useAuth();
  const [entries, setEntries] = useState(null);

  const load = () => api.get("/superadmin/seo").then(({ data }) => setEntries(data.entries));
  useEffect(() => { load(); }, []);

  if (user?.role !== "superadmin") return <p className="text-graphite">Accès réservé au super administrateur.</p>;

  const onSave = async (key, payload) => {
    try {
      await api.put(`/superadmin/seo/${key}`, payload);
      toast.success("SEO enregistré");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="super-seo" className="max-w-3xl">
      <p className="text-xs uppercase tracking-[0.2em] text-ash mb-2">Super Admin</p>
      <div className="flex items-center gap-3 mb-2">
        <Globe className="h-7 w-7 text-copper" strokeWidth={1.4} />
        <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">Gestion SEO</h1>
      </div>
      <p className="text-graphite text-sm mb-8">Optimisez le référencement page par page : titre, description, mots-clés et image de partage.</p>

      {!entries ? (
        <div className="space-y-3">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-16 bg-white border border-stone animate-pulse rounded-sm" />)}</div>
      ) : (
        <div className="space-y-3">
          {entries.map((e) => <SeoRow key={e.key} entry={e} onSave={onSave} />)}
        </div>
      )}
    </div>
  );
}
